import React, { useEffect, useState } from 'react';
import {
    CheckCircle2,
    XCircle,
    Rocket,
    Target,
    TrendingUp,
    Layers,
    Camera,
    HelpCircle,
    BarChart3,
    Compass,
    ShieldCheck,
    Calendar,
    Workflow,
    AlertTriangle,
    Plus,
    Minus,
    Trophy,
    Hash,
    Zap,
    Inbox,
    Mic,
    Scissors,
    Film,
    Palette,
    Clock
} from 'lucide-react';

/* Reusable Primitives */

const SectionTag = ({ icon: IconComp, label, color = "indigo" }) => {
    const colorClasses = {
        indigo: "bg-indigo-500/10 border-indigo-500/20 text-indigo-300",
        rose: "bg-rose-500/10 border-rose-500/20 text-rose-300",
        emerald: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
        amber: "bg-amber-500/10 border-amber-500/20 text-amber-300",
        purple: "bg-purple-500/10 border-purple-500/20 text-purple-300"
    };
    return (
        <div className={`inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border text-xs sm:text-sm font-semibold tracking-wide uppercase ${colorClasses[color]}`}>
            <IconComp className="w-4 h-4" /> {label}
        </div>
    );
};

const Check = ({ children, highlight = false }) => (
    <li className="flex gap-3 items-start">
        <CheckCircle2 className={`w-5 h-5 mt-0.5 flex-shrink-0 ${highlight ? 'text-indigo-400' : 'text-emerald-400'}`} />
        <span className={`leading-relaxed ${highlight ? 'text-indigo-100 font-medium' : 'text-slate-200 text-sm'}`}>{children}</span>
    </li>
);

const StatCard = ({ value, label, sub, accent = "indigo" }) => {
    const colors = {
        indigo: "from-indigo-900/40 to-slate-900/80 border-indigo-500/30",
        emerald: "from-emerald-900/40 to-slate-900/80 border-emerald-500/30",
        purple: "from-purple-900/40 to-slate-900/80 border-purple-500/30",
        amber: "from-amber-900/40 to-slate-900/80 border-amber-500/30",
        rose: "from-rose-900/40 to-slate-900/80 border-rose-500/30"
    };
    return (
        <div className={`bg-gradient-to-br ${colors[accent]} border rounded-2xl p-5 sm:p-6 text-center h-full flex flex-col justify-center`}>
            <div className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-1 whitespace-nowrap leading-none">{value}</div>
            <div className="text-xs sm:text-sm font-semibold text-white/80 mt-2">{label}</div>
            {sub && <div className="text-[10px] sm:text-xs text-white/50 mt-1.5">{sub}</div>}
        </div>
    );
};

const PrimaryCTA = () => (
    <div className="flex justify-center">
        <a
            href="#plan"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold text-sm shadow-lg shadow-indigo-500/30"
        >
            <Target className="w-4 h-4" /> See the plan
        </a>
    </div>
);

const AccordionItem = ({ title, body, open, onToggle }) => (
    <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
        <button
            onClick={onToggle}
            className="w-full flex items-start justify-between gap-3 text-left p-4 sm:p-5 hover:bg-white/5 transition"
        >
            <span className="font-bold text-white text-sm sm:text-base leading-snug">{title}</span>
            <span className="flex-shrink-0 mt-0.5">
                {open ? <Minus className="w-5 h-5 text-indigo-300" /> : <Plus className="w-5 h-5 text-indigo-300" />}
            </span>
        </button>
        {open && (
            <div className="px-4 sm:px-5 pb-5 text-slate-300 text-sm leading-relaxed">
                {body}
            </div>
        )}
    </div>
);

/* Main Component */
export default function RadhaPersonalBrand() {

    useEffect(() => {
        document.title = "Radha Patel | Personal Brand Proposal";
        window.scrollTo(0, 0);
    }, []);

    const [openIdx, setOpenIdx] = useState(null);
    const toggle = (i) => setOpenIdx(openIdx === i ? null : i);

    const circles = [
        {
            icon: Target,
            ring: "① The core · WHY",
            q: "Why build this at all?",
            a: "To show your journey as you build, and to turn that into real leverage: the opportunities, collabs, intros and brand comps that come from people watching what you're building.",
            so: "Everything we make points at that, your journey and the doors it opens.",
            color: "emerald"
        },
        {
            icon: Layers,
            ring: "② WHAT you're known for",
            q: "What do you want people to look up to you for?",
            a: "Lifestyle. Philosophy. Business. In that order.",
            so: "Three pillars, your sequence. Lifestyle leads because it builds the recording reflex fastest.",
            color: "amber"
        },
        {
            icon: Compass,
            ring: "③ HOW it sounds",
            q: "Teacher, or friend?",
            a: "Friend. You grow alongside your audience, you never talk down to them.",
            so: "Discovery framing always. 'I went down this rabbit hole,' never 'here's how you should.'",
            color: "purple"
        },
        {
            icon: Camera,
            ring: "④ HOW it gets made",
            q: "How do we make this actually sustainable for you?",
            a: "Take the friction off completely so recording feels weightless.",
            so: "You record raw. We build the entire machine around it. Habit first, then we experiment.",
            color: "indigo"
        }
    ];

    const circleColorMap = {
        emerald: "border-emerald-500/30 bg-emerald-900/10 text-emerald-300",
        amber: "border-amber-500/30 bg-amber-900/10 text-amber-300",
        purple: "border-purple-500/30 bg-purple-900/10 text-purple-300",
        indigo: "border-indigo-500/30 bg-indigo-900/10 text-indigo-300"
    };

    const yourSide = [
        { icon: Mic, t: "Open your camera, talk for 5 to 10 minutes", s: "the moment a thought hits you, in your own voice" },
        { icon: Camera, t: "Drop the raw clip in our Drive group", s: "no edit, no lighting, no setup" },
        { icon: CheckCircle2, t: "Go back to your day", s: "that's the entire ask" }
    ];

    const ourSide = [
        { icon: Compass, t: "Find the angle and the hook", s: "we pull the strongest line straight from what you already said" },
        { icon: Scissors, t: "Turn one recording into 3 to 4 pieces", s: "a 30-minute ramble becomes four finished posts" },
        { icon: Palette, t: "Keep every piece on-brand and recognisable", s: "your look and your voice, consistent everywhere" },
        { icon: Film, t: "Publish across platforms on schedule", s: "IG first, then YouTube and long-form as we test" },
        { icon: Inbox, t: "Surface the opportunity DMs back to you", s: "you only ever touch the messages worth replying to" }
    ];

    const faqs = [
        {
            title: "What if I don't record something every day?",
            body: "Completely fine, and expected. We bank a 7 to 10 video buffer before launch and keep a backlog running, so something goes out even on the days you don't feel like talking. There's no daily quota. We're building a gentle habit, not a chore."
        },
        {
            title: "What happens if a video doesn't get views?",
            body: "It becomes information, not a failure. This is a long game for opportunities, not overnight virality. We watch what lands, lean into those angles, and quietly let go of what doesn't. A quiet video costs you almost nothing, because all you spent was a few minutes talking."
        },
        {
            title: "Do I have to write scripts or come up with ideas?",
            body: "No. Your real moments are the ideas. You just talk to the camera when something hits you, and we pull the hooks from your own words and shape everything after that. The thinking-what-to-post part is off your plate."
        },
        {
            title: "What about people I work with, or people back home, seeing this?",
            body: "Hard line: we never post commentary about anyone you hire, work with, or let go. The test video you already recorded does not go live. Your privacy on that is built into the way we edit, not left to chance."
        }
    ];

    const pageCandidates = [
        { handle: "@radha.patel", note: "dot variant, cleanest if available" },
        { handle: "@radhapatelco", note: "company suffix, reads as builder/operator" },
        { handle: "@radhabuilds", note: "verb-anchored, fits Business + Lifestyle pillars" },
        { handle: "@radha.unfiltered", note: "matches the friend-tone discovery angle" },
        { handle: "@thisisradha", note: "declarative, grow-with-your-audience style" },
        { handle: "@radhainmotion", note: "Dubai/Zambia lifestyle hint without naming geography" },
        { handle: "@radha.rough.notes", note: "wildcard, leans into the rabbit-hole/discovery format" },
        { handle: "@radhapatel.diary", note: "wildcard, diary framing pre-empts the teacher-tone trap" }
    ];

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Radha · Personal Brand</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Proposal</span>
                </div>
            </header>

            <main className="max-w-[1120px] mx-auto px-4 space-y-20 sm:space-y-28">

                {/* HERO */}
                <section className="text-center pt-6 sm:pt-10">
                    <SectionTag icon={Rocket} label="Personal Brand Engine · Prepared for Radha Patel" />
                    <h1 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight pb-2">
                        Build the page<br />that opens the doors.
                    </h1>
                    <p className="text-base sm:text-xl md:text-2xl text-white/60 max-w-3xl mx-auto leading-relaxed mb-4">
                        30 videos a month, every month, in whatever format is working. You hit record for five minutes. We do everything else.
                    </p>
                    <p className="text-sm sm:text-lg md:text-xl text-white/50 max-w-3xl mx-auto leading-relaxed mb-10">
                        Not built for virality. Built so the right collabs, intros, invites and brand comps actually start landing in your inbox.
                    </p>

                    <PrimaryCTA />

                    <div className="grid grid-cols-2 md:grid-cols-4 max-w-3xl mx-auto gap-3 sm:gap-4 mt-10">
                        <StatCard value="30" label="Videos a month" sub="every month, all formats" accent="indigo" />
                        <StatCard value="10 min" label="Your daily effort" sub="5 to 10 min, raw clips" accent="emerald" />
                        <StatCard value="3-4" label="Posts per recording" sub="from one 30-min clip" accent="purple" />
                        <StatCard value="0" label="On your plate" sub="no scripts, no posting" accent="amber" />
                    </div>
                </section>

                {/* FOUR CIRCLES */}
                <section>
                    <div className="text-center mb-10">
                        <SectionTag icon={Layers} label="Built from your own answers" color="purple" />
                        <h2 className="text-3xl sm:text-4xl font-black mb-4">Four questions. Your four answers. One plan.</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                            On our May 29 call I kept pulling you back to four questions until the goal was crystal clear. This whole plan is built from the inside out, starting at the center with the one that matters most.
                        </p>
                    </div>

                    {/* Concentric rings graphic */}
                    <div className="relative mx-auto w-[280px] h-[280px] sm:w-[360px] sm:h-[360px] mb-12">
                        <div className="absolute inset-0 rounded-full border border-indigo-400/25 bg-indigo-500/[0.04] flex justify-center pt-2.5 sm:pt-3">
                            <span className="text-[9px] sm:text-[11px] font-bold uppercase tracking-widest text-indigo-300/80">④ The Engine</span>
                        </div>
                        <div className="absolute inset-[12%] rounded-full border border-purple-400/30 bg-purple-500/[0.05] flex justify-center pt-2.5 sm:pt-3">
                            <span className="text-[9px] sm:text-[11px] font-bold uppercase tracking-widest text-purple-300/80">③ The Voice</span>
                        </div>
                        <div className="absolute inset-[26%] rounded-full border border-amber-400/30 bg-amber-500/[0.06] flex justify-center pt-2.5 sm:pt-3">
                            <span className="text-[9px] sm:text-[11px] font-bold uppercase tracking-widest text-amber-300/80">② Known For</span>
                        </div>
                        <div className="absolute inset-[40%] rounded-full border border-emerald-400/40 bg-emerald-500/10 flex flex-col items-center justify-center text-center px-2">
                            <span className="text-[9px] sm:text-[11px] font-bold uppercase tracking-widest text-emerald-300">① Why</span>
                            <span className="text-[8px] sm:text-[10px] text-emerald-100/70 leading-tight mt-0.5">your journey<br />+ leverage</span>
                        </div>
                    </div>

                    {/* Four explainer cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-5xl mx-auto">
                        {circles.map((c, i) => (
                            <div key={i} className={`rounded-2xl border p-5 sm:p-6 ${circleColorMap[c.color]}`}>
                                <div className="flex items-center gap-2 mb-3">
                                    <c.icon className="w-5 h-5" />
                                    <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest">{c.ring}</span>
                                </div>
                                <div className="text-white/50 text-xs sm:text-sm mb-1">I asked: "{c.q}"</div>
                                <div className="text-white font-bold text-base sm:text-lg leading-snug mb-3">{c.a}</div>
                                <div className="text-slate-300 text-sm leading-relaxed">
                                    <span className="font-semibold text-white/80">So: </span>{c.so}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* THE ASYMMETRY */}
                <section>
                    <div className="text-center mb-10">
                        <SectionTag icon={Zap} label="The trade" color="amber" />
                        <h2 className="text-3xl sm:text-4xl font-black mb-4">Your 10 minutes becomes our 10 hours.</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                            The whole point is to take content off your plate. You stop thinking about it. You live your life, and when something hits you, you hit record. Everything after that is ours.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 max-w-5xl mx-auto">
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-6 sm:p-8">
                            <div className="flex items-center gap-2 mb-5">
                                <Mic className="w-5 h-5 text-emerald-300" />
                                <h3 className="text-lg sm:text-xl font-bold text-white">Your side</h3>
                                <span className="ml-auto text-emerald-300 text-xs font-bold uppercase tracking-widest">~10 min</span>
                            </div>
                            <ul className="space-y-4">
                                {yourSide.map((item, i) => (
                                    <li key={i} className="flex gap-3 items-start">
                                        <div className="bg-emerald-500/15 p-1.5 rounded-lg shrink-0">
                                            <item.icon className="w-4 h-4 text-emerald-300" />
                                        </div>
                                        <div>
                                            <div className="text-white font-semibold text-sm sm:text-base leading-snug">{item.t}</div>
                                            <div className="text-slate-400 text-xs sm:text-sm mt-0.5">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-6 sm:p-8">
                            <div className="flex items-center gap-2 mb-5">
                                <Scissors className="w-5 h-5 text-indigo-300" />
                                <h3 className="text-lg sm:text-xl font-bold text-white">Our side</h3>
                                <span className="ml-auto text-indigo-300 text-xs font-bold uppercase tracking-widest">the rest</span>
                            </div>
                            <ul className="space-y-4">
                                {ourSide.map((item, i) => (
                                    <li key={i} className="flex gap-3 items-start">
                                        <div className="bg-indigo-500/15 p-1.5 rounded-lg shrink-0">
                                            <item.icon className="w-4 h-4 text-indigo-300" />
                                        </div>
                                        <div>
                                            <div className="text-white font-semibold text-sm sm:text-base leading-snug">{item.t}</div>
                                            <div className="text-slate-400 text-xs sm:text-sm mt-0.5">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="max-w-3xl mx-auto mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 sm:p-6 flex items-start gap-4">
                        <div className="bg-amber-500/15 p-2.5 rounded-xl shrink-0">
                            <Clock className="w-5 h-5 text-amber-300" />
                        </div>
                        <p className="text-amber-50/90 text-sm sm:text-base leading-relaxed">
                            Send us one long, rambling video and we'll find three or four finished pieces inside it. You record once. We handle the rest. That's the whole idea.
                        </p>
                    </div>
                </section>

                {/* THE SHAPE OF IT */}
                <section id="plan">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-10">
                            <SectionTag icon={Camera} label="The shape of it" />
                            <h2 className="text-3xl sm:text-4xl font-black mb-4">One plan. 30 videos a month. Every format that works.</h2>
                        </div>

                        <div className="space-y-5 text-slate-200 text-base sm:text-lg leading-relaxed">
                            <p>
                                This is a single, simple offer. <span className="text-white font-semibold">30 videos every month</span>, built entirely from the raw clips you record in the moment. No scripting friction on your end, no shot lists. You hit record when something hits you, drop it in our Drive group, and we take it from there.
                            </p>
                            <p>
                                <span className="text-white font-semibold">Month 1 is about one thing: locking the recording habit.</span> We keep it simple, yapping-format only, so recording starts to feel weightless. The previous page built friction in your head. The first 30 days exist to remove it, before we add anything else.
                            </p>
                            <p>
                                <span className="text-white font-semibold">Then we experiment, hard.</span> Once the habit holds, we test formats in every direction: curated pieces with planned hooks for the topics you want landing harder, and long-form cut from your longer recordings. We research what's working, double down on it, and drop what isn't. Your face, your look and your voice stay constant across all of it, so you're recognisable no matter the format.
                            </p>
                        </div>
                    </div>
                </section>

                {/* THREE THINGS WE WONT DO */}
                <section>
                    <div className="text-center mb-10">
                        <SectionTag icon={AlertTriangle} label="The hard lines" color="rose" />
                        <h2 className="text-3xl sm:text-4xl font-black mb-4">Three things we explicitly will NOT do</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                            Baked into the design, not optional. Drawn straight from your May 29 call.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
                        <div className="rounded-2xl border border-rose-500/30 bg-rose-900/10 p-5 sm:p-6">
                            <div className="flex items-center gap-2 mb-3">
                                <XCircle className="w-5 h-5 text-rose-400" />
                                <h3 className="text-base sm:text-lg font-bold text-white">No new animation work</h3>
                            </div>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                Leo's Pride animation is your separate retainer, on your stated back-burner per the May 29 call. This proposal is purely personal-brand content.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-rose-500/30 bg-rose-900/10 p-5 sm:p-6">
                            <div className="flex items-center gap-2 mb-3">
                                <XCircle className="w-5 h-5 text-rose-400" />
                                <h3 className="text-base sm:text-lg font-bold text-white">No commentary on people you hire, work with, or let go</h3>
                            </div>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                You drew this line on May 29 because of the hometown-people privacy concern, and we respect it. The hires-test video you already recorded does NOT publish.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-rose-500/30 bg-rose-900/10 p-5 sm:p-6">
                            <div className="flex items-center gap-2 mb-3">
                                <XCircle className="w-5 h-5 text-rose-400" />
                                <h3 className="text-base sm:text-lg font-bold text-white">No teacher-tone content</h3>
                            </div>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                Business and Philosophy pillars get reframed as discovery, never as advice. "I went down this rabbit hole," not "here's how you should." The grow-with-your-audience pattern protects compounding reach.
                            </p>
                        </div>
                    </div>
                </section>

                {/* PILLARS */}
                <section>
                    <div className="text-center mb-10">
                        <SectionTag icon={Layers} label="Content pillars" color="purple" />
                        <h2 className="text-3xl sm:text-4xl font-black mb-4">Three pillars, in your stated order</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                            Lifestyle first because it builds the recording reflex fastest. Business last because it gets the friend-tone reframe before anything ships.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-5 sm:p-6">
                            <div className="text-indigo-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-2">Pillar 1</div>
                            <h3 className="text-lg sm:text-xl font-bold text-white mb-3">Lifestyle</h3>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                Dubai mornings, Zambia visits, gym sessions, food, daily mood. The recording-habit anchor. Everything else builds on top of this.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-purple-500/30 bg-purple-900/10 p-5 sm:p-6">
                            <div className="text-purple-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-2">Pillar 2</div>
                            <h3 className="text-lg sm:text-xl font-bold text-white mb-3">Philosophy</h3>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                Rabbit holes you went down. Things you're trying to figure out. Cast as discovery, never instruction. "I learned this" is the frame.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-amber-500/30 bg-amber-900/10 p-5 sm:p-6">
                            <div className="text-amber-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-2">Pillar 3</div>
                            <h3 className="text-lg sm:text-xl font-bold text-white mb-3">Business</h3>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                Building the biscuit brand, operator decisions, weird problems you ran into. Always discovery framing. Never "here's how you should."
                            </p>
                        </div>
                    </div>
                </section>

                {/* THE FIRST 90 DAYS */}
                <section>
                    <div className="text-center mb-10">
                        <SectionTag icon={ShieldCheck} label="The first 90 days" color="emerald" />
                        <h2 className="text-3xl sm:text-4xl font-black mb-4">What we'll have built in your first 90 days</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                            Two phases. The first locks the habit and the look. The second turns it into a running engine. Every line here is a commitment on our side, on a clock.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 max-w-5xl mx-auto">
                        <div className="rounded-2xl border-2 border-emerald-500/40 bg-emerald-900/15 p-6 sm:p-8 backdrop-blur shadow-[0_0_40px_-10px_rgba(16,185,129,0.3)]">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="bg-emerald-500/20 p-2 rounded-lg">
                                    <BarChart3 className="w-5 h-5 text-emerald-300" />
                                </div>
                                <span className="text-emerald-300 text-xs font-bold uppercase tracking-widest">First 30 days · Foundation</span>
                            </div>
                            <ul className="space-y-3">
                                <Check>Your look locked: a colour palette and consistent on-screen identity, the recognition anchor</Check>
                                <Check>The recording habit locked in, with a simple intake rhythm on WhatsApp and Drive</Check>
                                <Check>A 7 to 10 video buffer banked before anything goes live</Check>
                                <Check>Roughly 20 to 30 finished pieces produced from your raw clips</Check>
                            </ul>
                        </div>

                        <div className="rounded-2xl border-2 border-indigo-500/40 bg-indigo-900/15 p-6 sm:p-8 backdrop-blur shadow-[0_0_40px_-10px_rgba(99,102,241,0.3)]">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="bg-indigo-500/20 p-2 rounded-lg">
                                    <TrendingUp className="w-5 h-5 text-indigo-300" />
                                </div>
                                <span className="text-indigo-300 text-xs font-bold uppercase tracking-widest">Day 30 to 90 · Engine running</span>
                            </div>
                            <ul className="space-y-3">
                                <Check highlight>30 finished pieces a month, every month, no exceptions</Check>
                                <Check>Format experimentation: yapping, curated, and long-form cut from your longer recordings</Check>
                                <Check>Narrative and positioning refined off whatever is actually landing</Check>
                                <Check>Multi-platform distribution live, with a weekly Notion summary of what shipped and what's working</Check>
                            </ul>
                        </div>
                    </div>

                    <div className="max-w-3xl mx-auto mt-6 flex items-center gap-3 justify-center text-center">
                        <Trophy className="w-5 h-5 text-emerald-300 shrink-0" />
                        <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                            The scorecard here is on us, not on you. Your only job is to keep hitting record.
                        </p>
                    </div>
                </section>

                {/* PRICING */}
                <section id="investment">
                    <div className="text-center mb-8">
                        <SectionTag icon={Calendar} label="Investment" color="amber" />
                        <h2 className="text-3xl sm:text-4xl font-black mb-4">One number. Same shape as your animation retainer.</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                            Pay monthly, or prepay three months and the rate drops 20%, exactly like the Leo's Pride retainer. Bundled into the same monthly transfer with a separate invoice line so accounting stays clean.
                        </p>
                    </div>

                    <div className="max-w-md mx-auto rounded-3xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-900/30 to-slate-900/80 p-8 sm:p-10 text-center shadow-[0_0_50px_-12px_rgba(16,185,129,0.4)]">
                        <div className="inline-block px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-4 bg-emerald-500/10 text-emerald-300">
                            Personal Brand Engine
                        </div>
                        <div className="text-4xl sm:text-5xl font-black text-white">
                            USD 1,500<span className="text-lg sm:text-xl text-white/40 font-bold"> / mo</span>
                        </div>
                        <div className="text-sm text-white/50 mt-1">₹1,25,000 per month</div>

                        <div className="my-6 border-t border-white/10" />

                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 sm:p-5">
                            <div className="text-emerald-300 text-xs font-bold uppercase tracking-widest mb-2">Prepay 3 months, save 20%</div>
                            <div className="text-3xl sm:text-4xl font-black text-emerald-200">USD 1,200 <span className="text-base text-white/50 font-bold">/ mo</span></div>
                            <div className="text-sm text-white/50 mt-1">₹1,00,000 a month</div>
                            <div className="text-xs text-white/40 mt-2">USD 3,600 (₹3,00,000) upfront for the quarter</div>
                        </div>

                        <ul className="space-y-2.5 text-left mt-6">
                            <Check>30 finished videos a month, all formats</Check>
                            <Check>All the research, clipping, hooks and posting</Check>
                            <Check>Multi-platform distribution</Check>
                            <Check>A weekly Notion summary of what's working</Check>
                        </ul>

                        <p className="text-white/40 text-xs mt-6 leading-relaxed">
                            Payments via the existing Tech Sprout Media LLC channel, invoiced by SocialSEO LLP. Eureka pre-launch marketing is quoted separately once the packaging artwork is finalised.
                        </p>
                    </div>
                </section>

                {/* NEXT STEP */}
                <section>
                    <div className="text-center mb-10">
                        <SectionTag icon={Workflow} label="Next step" color="emerald" />
                        <h2 className="text-3xl sm:text-4xl font-black mb-4">What happens on the kickoff call</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                            One short call to lock the four things we need before the very first recording.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto">
                        {[
                            { n: "1", t: "The new IG handle", s: "8 candidate names in the appendix below" },
                            { n: "2", t: "The colour palette", s: "we'll show 3 options on the call" },
                            { n: "3", t: "The Drive group", s: "so you can start banking the buffer the next day" },
                            { n: "4", t: "Your recording rhythm", s: "the times of day a clip is most likely to happen" }
                        ].map((item, i) => (
                            <div key={i} className="rounded-xl border border-indigo-500/20 bg-indigo-900/10 p-4 sm:p-5">
                                <div className="text-indigo-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-2">Step {item.n}</div>
                                <h3 className="text-white font-bold text-sm sm:text-base mb-1.5 leading-snug">{item.t}</h3>
                                <p className="text-slate-400 text-xs sm:text-sm leading-snug">{item.s}</p>
                            </div>
                        ))}
                    </div>

                    <p className="text-center text-slate-400 text-sm sm:text-base mt-6 max-w-2xl mx-auto">
                        Suresh will send 2 to 3 window options for the call. If anything here doesn't feel right, tell me what's missing and we'll adjust it together, same standard as everything else we do.
                    </p>
                </section>

                {/* FAQ */}
                <section>
                    <div className="max-w-3xl mx-auto">
                        <div className="text-center mb-8">
                            <SectionTag icon={HelpCircle} label="FAQ" color="indigo" />
                            <h2 className="text-2xl sm:text-3xl font-black mb-3">The questions you're probably asking</h2>
                            <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                                The four that come up most, answered straight.
                            </p>
                        </div>

                        <div className="space-y-3">
                            {faqs.map((item, i) => (
                                <AccordionItem
                                    key={i}
                                    title={item.title}
                                    body={item.body}
                                    open={openIdx === i}
                                    onToggle={() => toggle(i)}
                                />
                            ))}
                        </div>
                    </div>
                </section>

                {/* APPENDIX — PAGE NAMES */}
                <section>
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-8">
                            <SectionTag icon={Hash} label="Appendix" color="purple" />
                            <h2 className="text-2xl sm:text-3xl font-black mb-3">Page name candidates</h2>
                            <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                                The handle <span className="text-white font-mono">@radhapatel</span> is held by a private user with 7 posts. Eight candidates for the new page, we'll check availability live on the kickoff call.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            {pageCandidates.map((c, i) => (
                                <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-4 flex items-start gap-3">
                                    <div className="text-indigo-300 font-bold flex-shrink-0">{i + 1}.</div>
                                    <div>
                                        <div className="text-white font-mono font-semibold text-sm sm:text-base">{c.handle}</div>
                                        <div className="text-slate-400 text-xs sm:text-sm mt-0.5">{c.note}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="text-center pb-10">
                    <div className="max-w-2xl mx-auto bg-gradient-to-br from-indigo-900/50 to-slate-900/80 border border-indigo-500/30 rounded-3xl p-8 sm:p-10 md:p-14 shadow-2xl">
                        <Rocket className="w-9 h-9 sm:w-10 sm:h-10 text-indigo-400 mx-auto mb-4" />
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 text-white">Let's lock it and start recording</h2>
                        <p className="text-slate-300 text-base sm:text-lg mb-2">
                            One call to set the handle, the palette and the Drive group. Then you just hit record.
                        </p>
                        <p className="text-slate-400 text-sm">
                            Suresh Malani · SocialSEO LLP
                        </p>
                    </div>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-8 border-t border-white/10">
                    <div className="font-bold text-white/70 mb-2">SocialSEO LLP</div>
                    <div className="mb-1">Dubai + Bangalore</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
